<template>
  <ckeditor
    :editor="editor"
    :config="editorConfig"
    @ready="editorInit"
    :disabled="isReadOnly"
    v-bind="$attrs"
    v-on="$listeners"
  />
</template>

<script>
// ⛔ 기존 build/커스텀 제거
// import DecoupledDocument from '@ckeditor/ckeditor5-build-decoupled-document'
// import Base64Upload from '@/plugins/ckeditor5/base64Upload/Base64Upload'

// ✅ 소스 방식 + 공식 플러그인 구성
import DecoupledEditor from '@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor';

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Link from '@ckeditor/ckeditor5-link/src/link';

import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';

// 추가 import
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import List from '@ckeditor/ckeditor5-list/src/list';
import ListProperties from '@ckeditor/ckeditor5-list/src/listproperties';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableColumnResize from '@ckeditor/ckeditor5-table/src/tablecolumnresize';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import Font from '@ckeditor/ckeditor5-font/src/font';
import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';

// ⬇️ 공식 Base64 업로드 어댑터 (커스텀 X)

export default {
  props: {
    isReadOnly: { type: Boolean, default: false },
    // 외부에서 추가 config 주면 병합(외부값 우선)
    config: { type: Object, default: null },
  },

  data() {
    const baseConfig = {
      licenseKey: `GPL`,
      plugins: [
        Essentials,
        Heading,
        Paragraph,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        Link,
        Image,
        ImageToolbar,
        ImageStyle,
        ImageUpload,
        ImageResize,
        Alignment,
        List,
        ListProperties,
        Indent,
        IndentBlock,
        Table,
        TableToolbar,
        TableProperties,
        TableCellProperties,
        TableColumnResize,
        MediaEmbed,
        Font,
        Highlight,
      ],
      toolbar: [
        'heading',
        '|',
        'fontFamily',
        'fontSize',
        'fontColor',
        'fontBackgroundColor',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'alignment',
        '|',
        'numberedList',
        'bulletedList',
        '|',
        'outdent',
        'indent',
        '|',
        'link',
        'blockQuote',
        '|',
        'uploadImage',
        'insertTable',
        'mediaEmbed',
        '|',
        'undo',
        'redo',
      ],
      image: {
        toolbar: [
          'imageStyle:inline',
          'imageStyle:block',
          'imageStyle:side',
          '|',
          'imageTextAlternative',
        ],
      },
      link: {
        decorators: {
          openInNewTab: {
            mode: 'manual',
            defaultValue: true,
            attributes: {
              target: '_blank',
              rel: 'noopener noreferrer',
            },
          },
        },
      },
      fontFamily: { supportAllValues: true },
      fontSize: {
        options: [10, 12, 14, 18, 24, 32],
        supportAllValues: true,
      },
      placeholder: '내용을 입력하세요...',
    };

    // 외부 config 병합 (겹치면 외부설정이 우선)
    const merged = { ...baseConfig, ...(this.config || {}) };

    return {
      editor: DecoupledEditor,
      editorConfig: merged,
    };
  },

  methods: {
    editorInit(editor) {
      try {
        const toolbar = editor.ui.view.toolbar.element;
        const editable = editor.ui.getEditableElement();
        const parent = editable.parentElement;

        // 이미 붙어있지 않으면 한 번만 붙이기 (중복 방지)
        if (toolbar.parentElement !== parent) {
          parent.insertBefore(toolbar, editable);
        }

        const setToolbarVisible = flag => {
          toolbar.style.display = flag ? 'none' : '';
        };

        // prop 기반으로 제어
        editor.enableReadOnlyMode && this.isReadOnly
          ? editor.enableReadOnlyMode('app')
          : editor.disableReadOnlyMode && editor.disableReadOnlyMode('app');

        setToolbarVisible(this.isReadOnly);

        editor.on('change:isReadOnly', (_, __, ro) => setToolbarVisible(ro));
      } catch (e) {
        console.warn('CKEditor toolbar attach failed:', e);
      }
    },
  },
};
</script>

<style scoped>
:deep(.ck.ck-editor__editable_inline) {
  min-height: 220px;
}

:global(.ck-read-only .ck-widget_with-resizer.ck-widget_selected) {
  outline: none !important;
  box-shadow: none !important;
  border: none !important;
}
</style>
